'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useCustomersList } from '@/lib/api/customers';
import { useTablePage } from '@/hooks/use-table-page';
import { CustomersTable } from './components/CustomersTable';
import { CreateCustomerDialog } from './components/create-customer-dialog';
import { ExportCustomersDialog } from './components/export-customers-dialog';
import { customersFilterMapping, customersQueryParamsMapping, customersSortMapping } from './utils/table-mappings';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CustomersPage, PERMISSIONS.CUSTOMERS_VIEW);

function CustomersPage() {
  console.log('CustomersPage rendered');
  const { t } = useTranslation(['customers', 'common']);

  // Use generic table page hook
  const { searchQuery, setSearchQuery, tableState, setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: customersFilterMapping,
    sortMapping: customersSortMapping,
    queryParamsMapping: customersQueryParamsMapping,
  });

  // Use tRPC hooks to fetch customers
  const {
    data: customersResponse,
    isLoading: loading,
    isFetching: fetching,
    error,
    refetch,
  } = useCustomersList(queryInput);

  const customers = customersResponse?.customers || [];
  const total = customersResponse?.total || 0;

  const [exportOpen, setExportOpen] = React.useState(false);
  const handleExportCustomers = React.useCallback(() => setExportOpen(true), []);

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <PageHeader title={t('customers:title')} description={t('customers:description')}>
        <CreateCustomerDialog />
      </PageHeader>

      {/* Stats cards */}
      {/* <ResourceStatsCardGrid cards={statsCards} loading={loading} /> */}

      {/* Customers table */}
      <TableCard>
        <CustomersTable
          customers={customers}
          total={total}
          loading={loading || fetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTableStateChange={setTableState}
          onRefetch={refetch}
          onExportCustomers={handleExportCustomers}
        />

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('customers:loadError')}
          </div>
        )}
      </TableCard>

      <ExportCustomersDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}
